import { useState, useEffect } from "react";
import apiFetch from "./apiClient";
import { Plus,Edit2,Trash2,ChevronUp,ChevronDown} from "lucide-react";
import { BarChart,Bar,PieChart,Pie,Cell,XAxis,YAxis,CartesianGrid,Tooltip,ResponsiveContainer } from "recharts";
import {CategoryCard} from './finance_control/CategoryCard';
import {OverviewCard} from './finance_control/OverviewCard';
import {AddIncomeCard} from './finance_control/AddIncomeCard';
import {AddExpenseCard} from './finance_control/AddExpenseCard';
import {AddInvestmentCard} from './finance_control/AddInvestmentCard';
import {ComparisonCard} from './finance_control/ComparisonCard';
import {CategoryGoalsCard} from './finance_control/CategoryGoalsCard';

const CHART_COLORS = ['var(--chart-1)','var(--chart-2)','var(--chart-3)','var(--chart-4)','var(--chart-5)','var(--chart-6)','var(--chart-7)','var(--chart-8)'];

const MONTHS = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

const FinanceControl = () => {
    const now = new Date();
    const [viewMode, setViewMode] = useState("monthly");
    const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
    const [selectedYear, setSelectedYear] = useState(now.getFullYear());
    const [incomes, setIncomes] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [investments, setInvestments] = useState([]);
    const [showIncomeForm, setShowIncomeForm] = useState(false);
    const [showExpensesForm, setShowExpensesform] = useState(false);
    const [showInvestmentForm, setShowInvestmentForm] = useState(false);
    const [editingIncome, setEditingIncome] = useState(null);
    const [editingExpense, setEditingExpense] = useState(null);
    const [editingInvestment, setEditingInvestment] = useState(null);
    const [error, setError] = useState(null);
    const [categories, setCategories] = useState([]);
    const [expenseSortKey, setExpenseSortKey] = useState(null);
    const [expenseSortDirection, setExpenseSortDirection] = useState('asc');

    const getIncomes = async () => {
        try {
            const response = await apiFetch("/api/financecontrol/get-incomes");
            setIncomes(response.data || []);
        } catch (err) {
            setError(err.message);
        }
    };
    const getExpenses = async () => {
        try {
            const response = await apiFetch("/api/financecontrol/get-expenses");
            setExpenses(response.data || []);
        } catch (err) {
            setError(err.message);
        }
    };
    const getInvestments = async () => {
        try {
            const response = await apiFetch("/api/financecontrol/get-investments");
            setInvestments(response.data || []);
        } catch (err) {
            setError(err.message);
        }
    };
    const getCategories = async () => {
        try {
            const response = await apiFetch("/api/financecontrol/get-categories");
            setCategories(response.data || []);
        } catch (err) {
            setError(err.message);
        }
    };

    useEffect(() => {
        getCategories();
        getIncomes();
        getExpenses();
        getInvestments();
    }, []);

    const filterByPeriod = (items) => {
        if (!items) return [];
        return items.filter(item => {
            const d = new Date(item.transacao_data);
            if (viewMode === 'annual') return d.getFullYear() === selectedYear;
            return d.getFullYear() === selectedYear && d.getMonth() === selectedMonth;
        });
    };

    const filteredIncomes = filterByPeriod(incomes);
    const filteredExpenses = filterByPeriod(expenses);
    const filteredInvestments = filterByPeriod(investments);

    const toggleExpenseSort = (key) => {
        if (expenseSortKey === key) {
            setExpenseSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setExpenseSortKey(key);
            setExpenseSortDirection('asc');
        }
    };

    const sortedExpenses = [...filteredExpenses].sort((a, b) => {
        if (!expenseSortKey) return 0;
        let valA = a[expenseSortKey];
        let valB = b[expenseSortKey];
        if (expenseSortKey === 'transacao_data') {
            valA = new Date(valA).getTime();
            valB = new Date(valB).getTime();
        } else if (expenseSortKey === 'categoria_nome') {
            valA = (valA || '').toLowerCase();
            valB = (valB || '').toLowerCase();
        }
        if (valA < valB) return expenseSortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return expenseSortDirection === 'asc' ? 1 : -1;
        return 0;
    });

    const SortIcon = ({ column }) => {
        if (expenseSortKey !== column) return null;
        return expenseSortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
    };

    const yearOptions = Array.from(new Set([
        ...incomes.map(i => new Date(i.transacao_data).getFullYear()),
        ...expenses.map(e => new Date(e.transacao_data).getFullYear()),
        ...investments.map(i => new Date(i.transacao_data).getFullYear()),
        now.getFullYear()
    ])).sort((a, b) => b - a);

    const deleteIncome = async (transacao_id) => {
        try {
            await apiFetch(`/api/financecontrol/delete-income/${transacao_id}`, { method: 'DELETE' });
            await getIncomes();
        } catch (err) {
            setError(err.message);
        }
    };
    const deleteExpense = async (transacao_id) => {
        try {
            await apiFetch(`/api/financecontrol/delete-expense/${transacao_id}`, { method: 'DELETE' });
            await getExpenses();
        } catch (err) {
            setError(err.message);
        }
    };
    const deleteInvestment = async (transacao_id) => {
        try {
            await apiFetch(`/api/financecontrol/delete-investment/${transacao_id}`, { method: 'DELETE' });
            await getInvestments();
        } catch (err) {
            setError(err.message);
        }
    };
    const startEditIncome = (income) => {
        setEditingIncome(income);
        setShowIncomeForm(true);
    };
    const startEditExpense = (expense) => {
        setEditingExpense(expense);
        setShowExpensesform(true);
    };
    const startEditInvestment = (investment) => {
        setEditingInvestment(investment);
        setShowInvestmentForm(true);
    };

    const pieData = [
        { name: 'Receitas', value: filteredIncomes.reduce((acc, inc) => acc + inc.transacao_valor, 0), color: 'var(--color-primary)'},
        { name: 'Gastos', value: filteredExpenses.reduce((acc, exp) => acc + exp.transacao_valor, 0), color: 'var(--color-danger)' }
    ];

    const expensesByCategory = filteredExpenses.reduce((acc, exp) => {
        const cat = exp.categoria_nome || 'Sem categoria';
        acc[cat] = (acc[cat] || 0) + exp.transacao_valor;
        return acc;
    }, {});
    const categoryData = Object.entries(expensesByCategory).map(([name, value], index) => ({
        name,
        value,
        color: CHART_COLORS[index % CHART_COLORS.length]
    }));

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-card p-3" style={{border: '1px, solid var(--color-border)'}}>
                    <p className="mb-1">{payload[0].payload.name}</p>
                    <p className="text-primary-custom fw-bold mb-0">
                        R$ {payload[0].value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                </div>
            );
        }
        return null;
    };

    const totalIncome = filteredIncomes.reduce((acc, inc) => acc + inc.transacao_valor, 0);
    const totalExpenses = filteredExpenses.reduce((acc, exp) => acc + exp.transacao_valor, 0);
    const totalInvestments = filteredInvestments.reduce((acc, inv) => acc + inv.transacao_valor, 0);
    const netBalance = totalIncome - totalExpenses - totalInvestments;

    return (
        <div className="p-4">
            <div className="container-fluid" style={{maxWidth:'1400px'}}>
                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}
                {/* Header with View Toggle */}
                <div className="row g-4 mb-4">
                    <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 col-12 col-md-6">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <button 
                                onClick={() => setViewMode("monthly")}
                                className={`btn ${viewMode === "monthly" ? "btn-primary-custom" : "btn-outline-custom"}`}
                            >
                                Mensal
                            </button>
                            <button 
                                onClick={() => setViewMode("annual")}
                                className={`btn ${viewMode === "annual" ? "btn-primary-custom" : "btn-outline-custom"}`}
                            >
                                Anual
                            </button>
                            {viewMode === "monthly" && (
                                <select
                                    className="form-select form-select-custom"
                                    style={{width: 'auto'}}
                                    value={selectedMonth}
                                    onChange={e => setSelectedMonth(Number(e.target.value))}
                                >
                                    {MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
                                </select>
                            )}
                            <select
                                className="form-select form-select-custom"
                                style={{width: 'auto'}}
                                value={selectedYear}
                                onChange={e => setSelectedYear(Number(e.target.value))}
                            >
                                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* Add new income category */}
                    <CategoryCard categories={categories} onCategoriesChanged={getCategories} />
                </div>

                {/* Overview Cards */}
                <OverviewCard 
                    viewMode={viewMode}
                    totalIncome={totalIncome}
                    totalExpenses={totalExpenses}
                    netBalance={netBalance}
                    incomes={filteredIncomes}
                    expenses={filteredExpenses}
                    selectedMonth={selectedMonth}
                    selectedYear={selectedYear}
                />

                {/* Charts Section */}
                <div className="row g-4 mb-4">
                    {/* Income vs Expenses Pie Chart */}
                    <div className="col-12 col-lg-6">
                        <div className="card custom-card h-100">
                            <div className="card-body p-4">
                                <h3 className="h5 fw-semibold mb-4">Receitas vs Gastos</h3>
                                <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie
                                            data={pieData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                            outerRadius={100}
                                            fill="#8884d8"
                                            dataKey="value"
                                        >
                                            {pieData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip content={<CustomTooltip />} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>

                    {/* Expenses by Category */}
                    <div className="col-12 col-lg-6">
                        <div className="card custom-card h-100">
                            <div className="card-body p-4">
                                <h3 className="h5 fw-semibold mb-4">Gastos por Categoria</h3>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={categoryData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                                        <XAxis 
                                            dataKey="name" 
                                            stroke="var(--color-text-secondary)"
                                            tick={{ fill: 'var(--color-text-secondary)', fontSize: 12 }}
                                        />
                                        <YAxis 
                                            stroke="var(--color-text-secondary)"
                                            tick={{ fill: 'var(--color-text-secondary)', fontSize: 12 }}
                                        />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                                            {categoryData.map((entry, index) => (
                                                <Cell key={`cat-cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Comparison Over Time */}
                <div className="row g-4 mb-4">
                    <div className="col-12 col-lg-6">
                        <ComparisonCard incomes={incomes} expenses={expenses} />
                    </div>

                {/* Category Goals */}
                    <div className="col-12 col-lg-6">
                        <CategoryGoalsCard categories={categories} expenses={filteredExpenses} viewMode={viewMode} />
                    </div>
                </div>

                {/* Income Section */}
                <div className="row g-4 mb-4">
                    <div className="col-12 col-lg-4">
                        <div className="card custom-card">
                            <div className="card-body p-4">
                                <div className="d-flex align-items-center justify-content-between mb-4">
                                    <h3 className="h5 fw-semibold mb-0">Fontes de Renda</h3>
                                    <button 
                                        className="btn btn-primary-custom d-flex align-items-center gap-2"
                                        onClick={() => setShowIncomeForm(!showIncomeForm)}
                                    >
                                        <Plus size={16} />
                                    </button>
                                </div>
                                
                                {/* Income form */}
                                {showIncomeForm && (
                                    <div className="mb-4 p-4 rounded" style={{backgroundColor: 'rgba(37,42,58,0.3)'}}>
                                        <h4 className="h6 mb-3">{editingIncome ? "Editar" : "Nova"} Renda</h4>
                                        <AddIncomeCard
                                            categories={categories}
                                            defaultMonth={selectedMonth}
                                            defaultYear={selectedYear}
                                            editingIncome={editingIncome}
                                            onSaved={async () => { setEditingIncome(null); setShowIncomeForm(false); await getIncomes(); }}
                                            onCancel={() => { setEditingIncome(null); setShowIncomeForm(false); }}
                                        />
                                    </div>
                                )}

                                {/* Income List */}
                                <div className="table-responsive">
                                    <table className="table table-custom table-hover bm-0">
                                        <thead>
                                            <tr style={{borderBottom: "1px solid var(--color-border)"}}>
                                                <th className="text-muted-custom small fw-medium border-bottom-none">Origem</th>
                                                <th className="text-muted-custom small fw-medium border-bottom-none">Valor</th>
                                                <th className="text-muted-custom small fw-medium text-end border-bottom-none">Ações</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredIncomes.map((income) =>
                                                <tr key={income.transacao_id} style={{borderBottom: "1px solid rgba(45,55,72,0.3)"}}>
                                                    <td className="border-bottom-none"><p className="mb-0">{income.categoria_nome}</p></td>
                                                    <td className="border-bottom-none"><p className="mb-0">R${income.transacao_valor.toLocaleString('pt-BR', {minimumFractionDigits:2,maximumFractionDigits:2})}</p></td>
                                                    <td className="text-end border-bottom-none">
                                                        <div className="d-flex gap-2 justify-content-end">
                                                            <button
                                                                className="btn btn-sm btn-outline-custom"
                                                                onClick={() => startEditIncome(income)}
                                                                aria-label="Editar renda"
                                                            >
                                                                <Edit2 size={14} />
                                                            </button>
                                                            <button
                                                                className="btn btn-sm"
                                                                onClick={() => deleteIncome(income.transacao_id)}
                                                                style={{
                                                                    backgroundColor: 'transparent',
                                                                    border: '1px solid var(--color-danger)',
                                                                    color: 'var(--color-danger)'
                                                                }}
                                                                aria-label="Excluir renda"
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {/* Investments Section */}
                        <div className="card custom-card mt-4">
                            <div className="card-body p-4">
                                <div className="d-flex align-items-center justify-content-between mb-4">
                                    <h3 className="h5 fw-semibold mb-0">Investimentos</h3>
                                    <button
                                        className="btn btn-primary-custom d-flex align-items-center gap-2"
                                        onClick={() => setShowInvestmentForm(!showInvestmentForm)}
                                    >
                                        <Plus size={16} />
                                    </button>
                                </div>

                                {/* Investment form */}
                                {showInvestmentForm && (
                                    <div className="mb-4 p-4 rounded" style={{backgroundColor: 'rgba(37,42,58,0.3)'}}>
                                        <h4 className="h6 mb-3">{editingInvestment ? "Editar" : "Novo"} Investimento</h4>
                                        <AddInvestmentCard
                                            categories={categories}
                                            defaultMonth={selectedMonth}
                                            defaultYear={selectedYear}
                                            editingInvestment={editingInvestment}
                                            onSaved={async () => { setEditingInvestment(null); setShowInvestmentForm(false); await getInvestments(); }}
                                            onCancel={() => { setEditingInvestment(null); setShowInvestmentForm(false); }}
                                        />
                                    </div>
                                )}

                                {/* Investment List */}
                                <div className="table-responsive">
                                    <table className="table table-custom table-hover bm-0">
                                        <thead>
                                            <tr style={{borderBottom: "1px solid var(--color-border)"}}>
                                                <th className="text-muted-custom small fw-medium border-bottom-none">Origem</th>
                                                <th className="text-muted-custom small fw-medium border-bottom-none">Valor</th>
                                                <th className="text-muted-custom small fw-medium text-end border-bottom-none">Ações</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredInvestments.map((investment) =>
                                                <tr key={investment.transacao_id} style={{borderBottom: "1px solid rgba(45,55,72,0.3)"}}>
                                                    <td className="border-bottom-none"><p className="mb-0">{investment.categoria_nome}</p></td>
                                                    <td className="border-bottom-none"><p className="mb-0">R${investment.transacao_valor.toLocaleString('pt-BR', {minimumFractionDigits:2,maximumFractionDigits:2})}</p></td>
                                                    <td className="text-end border-bottom-none">
                                                        <div className="d-flex gap-2 justify-content-end">
                                                            <button
                                                                className="btn btn-sm btn-outline-custom"
                                                                onClick={() => startEditInvestment(investment)}
                                                                aria-label="Editar investimento"
                                                            >
                                                                <Edit2 size={14} />
                                                            </button>
                                                            <button
                                                                className="btn btn-sm"
                                                                onClick={() => deleteInvestment(investment.transacao_id)}
                                                                style={{
                                                                    backgroundColor: 'transparent',
                                                                    border: '1px solid var(--color-danger)',
                                                                    color: 'var(--color-danger)'
                                                                }}
                                                                aria-label="Excluir investimento"
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Expenses Section */}
                    <div className="col-12 col-lg-8">
                        <div className="card custom-card">
                            <div className="card-body p-4">
                                <div className="d-flex align-items-center justify-content-between mb-4">
                                    <h3 className="h5 fw-semibold mb-0">Gastos</h3>
                                    <button 
                                        className="btn btn-primary-custom d-flex align-items-center gap-2"
                                        onClick={() => setShowExpensesform(!showExpensesForm)}
                                    >
                                        <Plus size={16} />
                                    </button>
                                </div>

                                {/* Expenses form */}
                                {showExpensesForm && (
                                    <div className="mb-4 p-4 rounded" style={{backgroundColor: 'rgba(37,42,58,0.3)'}}>
                                        <h4 className="h6 mb-3">{editingExpense ? "Editar" : "Novo"} Gastos</h4>
                                        <AddExpenseCard
                                            categories={categories}
                                            defaultMonth={selectedMonth}
                                            defaultYear={selectedYear}
                                            editingExpense={editingExpense}
                                            onSaved={async () => { setEditingExpense(null); setShowExpensesform(false); await getExpenses(); }}
                                            onCancel={() => { setEditingExpense(null); setShowExpensesform(false); }}
                                        />
                                    </div>
                                )}

                                {/* Expenses List */}
                                <div className="table-responsive">
                                    <table className="table table-custom table-hover mb-0">
                                        <thead>
                                            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                                                <th
                                                    className="text-muted-custom small fw-medium border-bottom-none"
                                                    role="button"
                                                    tabIndex={0}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => toggleExpenseSort('categoria_nome')}
                                                    onKeyDown={(e) => e.key === 'Enter' && toggleExpenseSort('categoria_nome')}
                                                >
                                                    <span className="d-flex align-items-center gap-1">Origem <SortIcon column="categoria_nome" /></span>
                                                </th>
                                                <th
                                                    className="text-muted-custom small fw-medium border-bottom-none"
                                                    role="button"
                                                    tabIndex={0}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => toggleExpenseSort('transacao_valor')}
                                                    onKeyDown={(e) => e.key === 'Enter' && toggleExpenseSort('transacao_valor')}
                                                >
                                                    <span className="d-flex align-items-center gap-1">Valor <SortIcon column="transacao_valor" /></span>
                                                </th>
                                                <th className="text-muted-custom small fw-medium border-bottom-none">Descrição</th>
                                                <th
                                                    className="text-muted-custom small fw-medium border-bottom-none"
                                                    role="button"
                                                    tabIndex={0}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => toggleExpenseSort('transacao_data')}
                                                    onKeyDown={(e) => e.key === 'Enter' && toggleExpenseSort('transacao_data')}
                                                >
                                                    <span className="d-flex align-items-center gap-1">Data <SortIcon column="transacao_data" /></span>
                                                </th>
                                                <th className="text-muted-custom small fw-medium text-end border-bottom-none">Ações</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedExpenses.map((expense) => (
                                                <tr key={expense.transacao_id} style={{ borderBottom: '1px solid rgba(45, 55, 72, 0.3)' }}>
                                                    <td style={{ borderBottom: 'none' }}>
                                                        <span className="badge badge-secondary-custom">{expense.categoria_nome}</span>
                                                    </td>
                                                    <td className="border-bottom-none">
                                                        <p className="mb-0">
                                                        R${expense.transacao_valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </p>
                                                    </td>
                                                    <td className="border-bottom-none">
                                                        <p className="text-muted-custom small mb-0">{expense.transacao_descricao}</p>
                                                    </td>
                                                    <td className="border-bottom-none">
                                                        <p className="text-muted-custom small mb-0">{new Date(expense.transacao_data).toLocaleDateString('pt-BR')}</p>
                                                    </td>
                                                    <td className="text-end border-bottom-none">
                                                        <div className="d-flex gap-2 justify-content-end">
                                                            <button
                                                                className="btn btn-sm btn-outline-custom"
                                                                onClick={() => startEditExpense(expense)}
                                                                aria-label="Editar gasto"
                                                            >
                                                                <Edit2 size={14} />
                                                            </button>
                                                            <button
                                                                className="btn btn-sm"
                                                                onClick={() => deleteExpense(expense.transacao_id)}
                                                                style={{
                                                                backgroundColor: 'transparent',
                                                                border: '1px solid var(--color-danger)',
                                                                color: 'var(--color-danger)'
                                                                }}
                                                                aria-label="Excluir gasto"
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FinanceControl;